/**
 * Recuperação e jornada do pedido ao vivo, montadas com os pedidos reais da Kiwify.
 *
 * Recuperação: cada pessoa (mesmo e-mail, pelo `buyer` do servidor) que teve cartão recusado, gerou Pix
 * ou boleto sem pagar ou abandonou o checkout nos últimos 7 dias vira um item da fila por produto ("uma
 * pessoa, um item" do curso e outro do 20X). O item fica aberto até 72 h; se ela pagou depois, fecha
 * como "Pagou sozinho" (ou "Recuperado", com contato antes). Quem já tinha comprado o mesmo produto, ou
 * pagou em até 15 min (nova tentativa da mesma compra), não entra. O andamento que a equipe marca
 * (contato feito, recuperado, perdido) e o que o servidor sabe de cada pedido (comprador do carrinho,
 * chegada do aviso, WhatsApp automático) vêm de `/api/painel/recuperacao`. O painel não guarda nome,
 * e-mail nem telefone: o dono vê o contato pela rota auditada `/api/painel/recuperacao/contato`.
 */

import type {
  CustomerPii,
  DataProvider,
  KpiId,
  KpiValue,
  ListQuery,
  Order,
  OrderJourney,
  Page,
  ProductKind,
  Query,
  RecoveryCommand,
  RecoveryItem,
  RecoveryStatus,
  RecoveryType,
  Settings,
} from '@rota/contracts';
import { getWithRetry } from './http';
import {
  buckets,
  isRefundLike,
  kv,
  matches,
  median,
  orderDates,
  paginate,
  paymentOf,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  rate,
  sparkSize,
  toOrder,
} from './realCore';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
/** Janela da fila: depois disso o item fecha como "expirado". */
const CLOSE_AFTER = 72 * HOUR;
/** Faltas (recusa, Pix, carrinho) que entram na fila: as dos últimos 7 dias. */
const QUEUE_WINDOW = 7 * DAY;
/** Compras olhadas para trás: quem comprou o mesmo produto até 7 dias antes da falha não entra. */
const LOOKBACK = 14 * DAY;
/** Pagou até 15 min depois da 1ª falha, sem contato: nova tentativa da mesma compra, não recuperação. */
const RETRY_MS = 15 * MIN;
/** Amostra mínima das taxas em 72 h (itens que já completaram 72 h). */
const RATE_MIN_SAMPLE = 30;
/** Cache do andamento e dos pedidos da fila (a lista, a faixa e o snapshot leem juntos). */
const DATA_CACHE_MS = 30_000;
/** Preço do produto quando o carrinho abandonado chega sem valor. */
// Premium: US$ 397 (≈ R$ 2.144 a R$ 5,40); o valor real de cada pedido já chega convertido pela PTAX do dia.
const LIST_PRICE: Record<ProductKind, number> = { front: 29_700, upsell: 69_700, premium: 214_400 };
/** Busca do pedido pelo id: janelas de 120 dias (o máximo que o servidor aceita), até 3 (um ano). */
const JOURNEY_WINDOW = 120 * DAY;
const JOURNEY_WINDOWS = 3;
const DEFAULT_DUE_MIN: Record<RecoveryType, number> = { refused: 15, pix: 30, cart: 60, boleto: 120 };
/** Autor do contato quando a mensagem saiu sozinha (recuperação automática do WhatsApp). */
export const AUTOMATION_BY = 'Automação do WhatsApp';

export interface RecoveryState {
  at?: number;
  assigneeId?: string | null;
  contacts?: RecoveryItem['contacts'];
  outcome?: RecoveryItem['outcome'];
}
export type RecoveryStates = Record<string, RecoveryState>;

/** O que o servidor sabe de um pedido da fila além da linha de vendas (sem dado pessoal). */
export interface RecoveryOrderInfo {
  /** Comprador do carrinho antigo, que chegou sem e-mail no pedido (pego do aviso do WhatsApp). */
  buyer?: string | null;
  /** Quando o aviso da Kiwify chegou (o carrinho chega ~1 h depois do checkout). */
  receivedAt?: number | null;
  /** O aviso trouxe telefone. */
  phone?: boolean;
  /** Recuperação automática do WhatsApp deste pedido. */
  wa?: {
    status: string;
    reason: string | null;
    sentAt: number | null;
    recoveredAt: number | null;
    recoveredOrderId: string | null;
    recoveredVia: string | null;
  } | null;
}
export type RecoveryOrders = Record<string, RecoveryOrderInfo>;

/**
 * Item da fila ao vivo: o do contrato com o que só o modo ao vivo sabe (fora do contrato; a tela lê
 * com `liveRecoveryInfo`).
 */
export interface LiveRecoveryItem extends RecoveryItem {
  /** Pedidos da Kiwify que formam o item (o contato é buscado por eles). */
  orderIds: string[];
  /** O aviso da Kiwify trouxe telefone (undefined = servidor antigo, sem a informação). */
  hasPhone?: boolean;
  /** Quando o aviso chegou: o prazo do 1º contato conta daqui (o carrinho chega ~1 h depois). */
  arrivedAt: number;
  /** Mensagem automática do WhatsApp agendada para o item (ainda não saiu). */
  autoScheduled?: boolean;
}

/** O que a tela usa do item ao vivo (vazio fora do modo ao vivo). */
export const liveRecoveryInfo = (item: RecoveryItem): Partial<LiveRecoveryItem> => item as LiveRecoveryItem;

/** Andamento salvo e pedidos da fila. Erro do servidor lança: quem chama mantém o último estado. */
export async function fetchRecoveryData(): Promise<{ states: RecoveryStates; orders: RecoveryOrders }> {
  const r = await getWithRetry('/api/painel/recuperacao');
  if (!r.ok) throw new Error(`recuperação ${r.status}`);
  const body = (await r.json()) as { items?: RecoveryStates; orders?: RecoveryOrders };
  return { states: body.items ?? {}, orders: body.orders ?? {} };
}

/** Andamento salvo da fila. Erro do servidor lança: quem chama mantém o último estado conhecido. */
export async function fetchRecoveryStates(): Promise<RecoveryStates> {
  return (await fetchRecoveryData()).states;
}

export async function saveRecoveryCommand(cmd: RecoveryCommand): Promise<RecoveryState> {
  const r = await fetch('/api/painel/recuperacao', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(cmd),
  });
  if (!r.ok) throw new Error(`recuperação ${r.status}`);
  return ((await r.json()) as { item: RecoveryState }).item;
}

/**
 * Nome e telefone de quem está no item, como vieram no aviso da Kiwify. Só o dono pode (o servidor
 * recusa os outros) e cada consulta fica na auditoria. Nada é guardado no painel.
 */
export async function revealRecoveryContact(item: RecoveryItem): Promise<CustomerPii> {
  const ids =
    liveRecoveryInfo(item).orderIds ?? [item.orderId ?? item.cartId].filter((x): x is string => !!x);
  const r = await fetch(`/api/painel/recuperacao/contato?pedidos=${encodeURIComponent(ids.join(','))}`, {
    credentials: 'include',
  });
  if (r.status === 404)
    throw Object.assign(new Error('Sem telefone no aviso da Kiwify.'), { code: 'not_found' });
  if (r.status === 403)
    throw Object.assign(new Error('Só o dono pode ver o contato do cliente.'), { code: 'forbidden' });
  if (!r.ok) throw new Error(`contato ${r.status}`);
  const c = (await r.json()) as { name: string | null; phone: string };
  return { name: c.name ?? '', email: '', phone: c.phone, document: null };
}

const CHANNEL: Record<RealOrderRow['channel'], string> = {
  automacao: 'Automação do direct',
  ig_bio: 'Bio do Instagram',
  meta_ads: 'Anúncio',
  outro: 'Outro link',
  sem_origem: 'Sem origem',
};
const originLabel = (o: RealOrderRow) =>
  o.channel === 'meta_ads' && (o.adName || o.campaignName)
    ? `Anúncio · ${o.adName ?? o.campaignName}`
    : CHANNEL[o.channel];

/** Tipo de recuperação de um pedido não pago (null = não entra na fila). */
function typeOf(o: RealOrderRow): RecoveryType | null {
  if (o.status === 'refused') return 'refused';
  if (o.status === 'waiting_payment')
    return (o.payment ?? '').toLowerCase().includes('boleto') ? 'boleto' : 'pix';
  if (o.rawStatus === 'abandoned') return 'cart';
  return null;
}
/** Produto do item: a consulta Premium é produto próprio (nunca o 20X). */
const product = (o: RealOrderRow): ProductKind =>
  o.kind === 'front' ? 'front' : o.kind === 'premium' ? 'premium' : 'upsell';
const who = (o: RealOrderRow) => o.buyer ?? `o:${o.id}`;
/**
 * Produtos que entram na fila. As mensagens prontas da recuperação falam do curso (parcelamento em
 * 12x, "Do Zero aos EUA"); a consulta Premium e os produtos que o servidor não reconhece ("outro")
 * ficam fora da fila até terem mensagem própria, para ninguém receber o texto do produto errado.
 */
const QUEUED = new Set<RealOrderRow['kind']>(['front', 'upsell']);
/** O WhatsApp automático viu que a pessoa já pagou (pelo e-mail ou pelo telefone). */
const WA_PAID = new Set(['pagou antes', 'já pagou']);

/** Linha da Kiwify de cada item (os filtros de origem e produto da lista olham para ela). */
const SOURCE_ROW = new WeakMap<RecoveryItem, RealOrderRow>();

/** Item segue os filtros da tela (origem, produto, forma de pagamento), pelo pedido mais recente. */
export const recoveryMatches = (item: RecoveryItem, q: Pick<Query, 'filters'>): boolean => {
  const row = SOURCE_ROW.get(item);
  return row ? matches(row, q as Query) : true;
};

export function buildRecovery(
  rows: RealOrderRow[],
  states: RecoveryStates,
  now: number,
  settings?: Pick<Settings, 'recovery'> | null,
  orders: RecoveryOrders = {},
): LiveRecoveryItem[] {
  const dueMin = settings?.recovery?.dueMin ?? DEFAULT_DUE_MIN;
  // O carrinho antigo chegou sem e-mail no pedido: o comprador vem do aviso do WhatsApp.
  const withBuyer = rows.map((o) =>
    o.buyer == null && orders[o.id]?.buyer ? { ...o, buyer: orders[o.id]?.buyer ?? null } : o,
  );
  // Compras pagas por pessoa (o aviso do WhatsApp é conferido contra elas) e pedidos por id.
  const paidByPerson = new Map<string, RealOrderRow[]>();
  const byId = new Map<string, RealOrderRow>();
  for (const o of withBuyer) {
    byId.set(o.id, o);
    if (o.status === 'paid' && o.buyer != null)
      paidByPerson.set(o.buyer, [...(paidByPerson.get(o.buyer) ?? []), o]);
  }
  // Um item por pessoa e produto: tentativas do curso e do 20X fecham cada uma pelo próprio produto.
  const byKey = new Map<string, RealOrderRow[]>();
  for (const o of withBuyer) {
    if (!QUEUED.has(o.kind)) continue;
    const key = `${who(o)}|${product(o)}`;
    byKey.set(key, [...(byKey.get(key) ?? []), o]);
  }
  const out: LiveRecoveryItem[] = [];
  for (const list of byKey.values()) {
    const sorted = [...list].sort((a, b) => a.at - b.at);
    const misses = sorted.filter((o) => typeOf(o) != null && now - o.at <= QUEUE_WINDOW);
    const first = misses[0];
    if (!first) continue;
    // Carrinho sem e-mail nenhum (nem no aviso do WhatsApp): não dá para juntar a pessoa, saber se
    // ela comprou nem falar com ela. Fica fora da fila em vez de inflar "Atrasados" e "Em jogo".
    if (first.buyer == null && typeOf(first) === 'cart') continue;
    const last = misses[misses.length - 1] ?? first;
    const type = typeOf(last) as RecoveryType;
    const prod = product(last);
    const person = who(first).replace(/^o:/, '');
    // O id do curso é o de antes (andamento salvo continua valendo); o 20X ganha o próprio.
    const id = prod === 'upsell' ? `rec_${person}_upsell` : `rec_${person}`;
    const st = states[id] ?? {};
    const saved = (st.contacts?.length ?? 0) > 0 || st.outcome != null;
    const paidBefore = sorted.find((o) => o.status === 'paid' && o.at < first.at);
    const paidAfter = sorted.find((o) => o.status === 'paid' && o.at >= first.at);
    const infos = misses.map((o) => orders[o.id]).filter((x): x is RecoveryOrderInfo => x != null);
    const was = infos.map((i) => i.wa).filter((w): w is NonNullable<RecoveryOrderInfo['wa']> => w != null);
    // O WhatsApp automático olha qualquer compra da pessoa, de qualquer produto. Quem comprou o curso
    // e logo depois teve o 20X recusado aparece lá como "já pagou", mas o 20X segue em aberto: o aviso
    // só vale quando nenhuma compra de outro produto (a partir de 1 h antes da falha) explica o
    // pagamento que ele viu, ou seja, foi o próprio produto pago com outro e-mail (achado pelo telefone).
    const otherPaid = (paidByPerson.get(who(first)) ?? []).some(
      (o) => product(o) !== prod && o.at >= first.at - HOUR,
    );
    const waPaid = was.find((w) => w.status === 'skipped' && WA_PAID.has(w.reason ?? '') && !otherPaid);
    // Já tinha comprado o mesmo produto antes da falha (outra compra, ou o servidor viu o pagamento
    // pelo telefone antes do envio): não é alguém para recuperar.
    if (!saved && !paidAfter && (paidBefore || waPaid?.reason === 'já pagou')) continue;
    // Mensagem automática que já saiu conta como contato (com a hora do envio).
    const auto: RecoveryItem['contacts'] = was
      .filter((w) => w.sentAt != null)
      .map((w) => ({ at: w.sentAt as number, by: AUTOMATION_BY, channel: 'whatsapp' as const }));
    const contacts = [...(st.contacts ?? []), ...auto].sort((a, b) => a.at - b.at);
    // Nova tentativa da mesma compra (outro cartão, Pix na hora): pagou logo, ninguém falou com ela.
    if (paidAfter && !saved && contacts.length === 0 && paidAfter.at - first.at <= RETRY_MS) continue;
    let status: RecoveryStatus = contacts.length ? 'contacted' : 'open';
    let outcome: RecoveryItem['outcome'] | undefined = st.outcome;
    // O servidor marca "recuperado" com qualquer compra da pessoa: só vale quando a compra que ele
    // ligou à mensagem é deste produto (senão a compra do curso fecharia o 20X como recuperado).
    const waWon = was.find((w) => {
      if (w.status !== 'recovered' || w.recoveredAt == null || !w.recoveredOrderId) return false;
      const won = byId.get(w.recoveredOrderId);
      return won != null && won.status === 'paid' && product(won) === prod;
    });
    if (paidAfter) {
      const before = contacts.filter((c) => c.at <= paidAfter.at);
      const helped = before.length > 0;
      status = helped ? 'recovered' : 'paid_alone';
      outcome = {
        at: paidAfter.at,
        by: helped ? (before[before.length - 1]?.by ?? 'system') : 'system',
        result: helped ? 'recovered' : 'paid_alone',
        recoveredOrderId: paidAfter.id,
      };
    } else if (waWon) {
      // O servidor ligou o pagamento à mensagem automática (pelo telefone, mesmo com outro e-mail).
      status = 'recovered';
      outcome = {
        at: waWon.recoveredAt as number,
        by: AUTOMATION_BY,
        result: 'recovered',
        ...(waWon.recoveredOrderId ? { recoveredOrderId: waWon.recoveredOrderId } : {}),
      };
    } else if (outcome) status = outcome.result;
    else if (waPaid) {
      // "Pagou antes" do envio: a compra chegou por outro e-mail (o servidor achou pelo telefone).
      // Pagou sem ninguém falar com ela; a hora exata não vem, fica a da chegada do aviso.
      status = 'paid_alone';
      outcome = {
        at: Math.max(first.at, orders[first.id]?.receivedAt ?? first.at),
        by: 'system',
        result: 'paid_alone',
      };
    } else if (now - first.at > CLOSE_AFTER) {
      status = 'expired';
      outcome = { at: first.at + CLOSE_AFTER, by: 'system', result: 'expired' };
    }
    // O prazo do 1º contato conta da chegada do aviso (o do carrinho chega ~1 h depois do checkout).
    const arrivedAt = Math.max(first.at, orders[first.id]?.receivedAt ?? first.at);
    // Valor do produto (sem os juros do parcelamento, que não são faturamento).
    const value = Math.max(...misses.map((o) => o.baseCents ?? o.grossCents), 0) || LIST_PRICE[prod];
    const open = status === 'open' || status === 'contacted';
    const autoScheduled = open && contacts.length === 0 && was.some((w) => w.status === 'scheduled');
    const item: LiveRecoveryItem = {
      id,
      type,
      status,
      mergedTypes: [...new Set(misses.map((o) => typeOf(o) as RecoveryType))],
      ...(last.status !== 'other' ? { orderId: last.id } : { cartId: last.id }),
      product: prod,
      value,
      customer: null,
      createdAt: first.at,
      dueAt: arrivedAt + (dueMin[type] ?? 30) * MIN,
      closesAt: first.at + CLOSE_AFTER,
      assigneeId: st.assigneeId ?? null,
      contacts,
      ...(outcome ? { outcome } : {}),
      traits: autoScheduled ? [{ label: 'WhatsApp automático agendado' }] : [],
      originLabel: originLabel(last),
      messageTemplateId: type,
      orderIds: misses.map((o) => o.id),
      ...(infos.length ? { hasPhone: infos.some((i) => i.phone === true) } : {}),
      arrivedAt,
      ...(autoScheduled ? { autoScheduled } : {}),
    };
    SOURCE_ROW.set(item, last);
    out.push(item);
  }
  return out.sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Resumo da fila (REC-01, HOJ-08, menu): abertos (os que já existiam em `now`), vencendo e atrasados
 * sem 1º contato, valor da fila aberta (soma do valor dos produtos, sem juros), abertos por tipo e o
 * mais antigo. Valor e contagens saem do mesmo conjunto.
 */
export function recoverySummary(items: RecoveryItem[], now: number) {
  const open = items.filter((i) => (i.status === 'open' || i.status === 'contacted') && i.createdAt <= now);
  const waiting = open.filter((i) => i.contacts.length === 0);
  const byType: Record<RecoveryType, number> = { refused: 0, pix: 0, cart: 0, boleto: 0 };
  let oldestAt: number | null = null;
  for (const i of open) {
    byType[i.type] += 1;
    if (oldestAt == null || i.createdAt < oldestAt) oldestAt = i.createdAt;
  }
  return {
    open: open.length,
    dueSoon: waiting.filter((i) => i.dueAt > now && i.dueAt - now <= 15 * MIN).length,
    overdue: waiting.filter((i) => i.dueAt <= now).length,
    moneyAtStake: open.reduce((s, i) => s + i.value, 0),
    byType,
    oldestAt,
  };
}

/** Itens que já completaram 72 h (coorte das taxas): só eles têm o desfecho de 72 h conhecido. */
function rate72h(items: readonly RecoveryItem[], now: number) {
  const mature = items.filter((i) => now - i.createdAt >= CLOSE_AFTER);
  // Recuperado = pagou em até 72 h depois de um contato (equipe ou automático). "Pagou sozinho" não conta.
  const won = mature.filter(
    (i) => i.status === 'recovered' && i.outcome != null && i.outcome.at - i.createdAt <= CLOSE_AFTER,
  );
  return { n: mature.length, won: won.length };
}

/** Nota das taxas: de onde sai a coorte (a fila guarda 7 dias). */
const RATE_NOTE = 'Itens que entraram na fila de 3 a 7 dias atrás; "Pagou sozinho" não conta';
/** Nota do detalhe da métrica: a fila é um retrato de agora. */
export const QUEUE_NOW_NOTE = 'Retrato da fila agora: não muda com o período escolhido';

/** Os KPIs da recuperação, todos da fila real (nunca da simulação). */
export function recoveryKpis(rec: readonly RecoveryItem[], now: number): Partial<Record<KpiId, KpiValue>> {
  const recSum = recoverySummary([...rec], now);
  const won = rec.filter((i) => i.status === 'recovered');
  const all = rate72h(rec, now);
  const carts = rate72h(
    rec.filter((i) => i.mergedTypes.includes('cart')),
    now,
  );
  const sampled = (id: KpiId, r: { n: number; won: number }): KpiValue => ({
    ...kv(id, rate(r.won, r.n), null, now),
    status: r.n < RATE_MIN_SAMPLE ? 'insufficient' : 'neutral',
    sample: { n: r.n, min: RATE_MIN_SAMPLE, unit: 'itens com 72 h' },
    note: RATE_NOTE,
  });
  // Até o 1º contato: da chegada do aviso ao 1º contato (equipe ou mensagem automática).
  const waits = rec.flatMap((i) => {
    const c = i.contacts[0];
    if (!c) return [];
    const base = (i as Partial<LiveRecoveryItem>).arrivedAt ?? i.createdAt;
    return [Math.max(0, (c.at - base) / MIN)];
  });
  const sla = median(waits);
  return {
    moneyAtStake: kv('moneyAtStake', recSum.moneyAtStake, null, now),
    queueOverdue: kv('queueOverdue', recSum.overdue, null, now),
    recoveredRevenue: kv(
      'recoveredRevenue',
      won.reduce((s, i) => s + i.value, 0),
      null,
      now,
    ),
    recoveredCount: kv('recoveredCount', won.length, null, now),
    recoveryRate72h: sampled('recoveryRate72h', all),
    cartRecoveryRate: sampled('cartRecoveryRate', carts),
    contactSlaMedianMin:
      sla == null
        ? { ...kv('contactSlaMedianMin', null, null, now), note: 'Nenhum contato registrado na fila ainda' }
        : kv('contactSlaMedianMin', Math.round(sla), null, now),
  };
}

/** Nome do produto na jornada, com o artigo ("o curso", "a consultoria"). */
const STEP_PRODUCT: Record<RealOrderRow['kind'], { o: string; no: string; do: string }> = {
  front: { o: 'o curso', no: 'no curso', do: 'do curso' },
  upsell: { o: 'o 20X', no: 'no 20X', do: 'do 20X' },
  premium: { o: 'a consultoria', no: 'na consultoria', do: 'da consultoria' },
  outro: { o: 'o produto', no: 'no produto', do: 'do produto' },
};

type JourneyStep = OrderJourney['steps'][number];

/** Passo da compra paga ("Pagou o curso", "Comprou a consultoria"; só o 20X é upsell_paid). */
const paidStep = (o: RealOrderRow, at: number): JourneyStep => {
  const p = STEP_PRODUCT[o.kind];
  if (o.kind === 'upsell') return { at, kind: 'upsell_paid', label: `Pagou ${p.o}` };
  return { at, kind: 'paid', label: `${o.kind === 'premium' ? 'Comprou' : 'Pagou'} ${p.o}` };
};

/**
 * Passos de um pedido, cada um na sua hora: o Pix gerado na criação e o pago na aprovação; o
 * reembolso com a compra antes (na data da compra) e a devolução na data dela.
 */
const STEPS_OF = (o: RealOrderRow): JourneyStep[] => {
  const p = STEP_PRODUCT[o.kind];
  const dates = orderDates(o);
  if (o.status === 'paid') {
    const paidAt = dates.paidAt ?? o.at;
    const pixFirst = o.payment != null && paymentOf(o.payment) === 'pix' && dates.createdAt < paidAt;
    return [
      ...(pixFirst
        ? [{ at: dates.createdAt, kind: 'pix_created' as const, label: `Gerou o pagamento ${p.do}` }]
        : []),
      paidStep(o, paidAt),
    ];
  }
  if (o.status === 'refused')
    return [{ at: dates.createdAt, kind: 'refused', label: `Cartão recusado ${p.no}` }];
  if (o.status === 'waiting_payment')
    return [{ at: dates.createdAt, kind: 'pix_created', label: `Gerou o pagamento ${p.do}` }];
  if (isRefundLike(o.status))
    return [
      paidStep(o, dates.paidAt ?? o.at),
      {
        at: dates.refundedAt ?? o.at,
        kind: 'refunded',
        label: o.status === 'refunded' ? 'Reembolso' : 'Chargeback',
      },
    ];
  if (o.rawStatus === 'abandoned')
    return [{ at: o.at, kind: 'checkout', label: `Abriu o checkout ${p.do} e saiu` }];
  return [];
};

/**
 * Passo sem hora conhecida (o clique de origem quando o cookie _fbc não chegou): quem desenha a
 * jornada testa com `Number.isFinite(at)` e não mostra hora nenhuma, em vez de repetir a da compra.
 */
export const NO_TIME = Number.NaN;
/** Até onde o _fbc vale como hora do clique: o cookie do anúncio dura 90 dias. */
const FBC_MAX_AGE = 90 * DAY;

/**
 * Hora do clique no anúncio, pelo cookie _fbc que chegou no "sck" do checkout
 * (`fb.<n>.<ms>.<fbclid>`; o _fbp tem o mesmo formato, mas só com números no fim, e fica de fora).
 * Null quando não há _fbc ou a hora não faz sentido (depois da compra ou velha demais).
 */
export function fbcClickAt(sck: string | null | undefined, purchaseAt: number): number | null {
  for (const m of (sck ?? '').matchAll(/(?:^|[~|,\s])fb\.\d\.(\d{10,13})\.([\w-]{10,})/g)) {
    if (!/[A-Za-z_-]/.test(m[2] ?? '')) continue;
    const raw = Number(m[1]);
    const at = (m[1] ?? '').length <= 10 ? raw * 1000 : raw;
    if (at <= purchaseAt && purchaseAt - at <= FBC_MAX_AGE) return at;
  }
  return null;
}

/** Jornada real de um pedido: de onde veio e tudo o que a mesma pessoa fez na Kiwify. */
export function buildJourney(rows: RealOrderRow[], orderId: string): OrderJourney | null {
  const row = rows.find((o) => o.id === orderId);
  const order = row ? toOrder(row) : null;
  if (!row || !order) return null;
  const mine = rows.filter((o) => who(o) === who(row)).sort((a, b) => a.at - b.at);
  const first = mine[0] ?? row;
  const steps: OrderJourney['steps'] = [];
  // A hora do clique só existe com o _fbc; sem ele o passo de origem fica sem hora (nunca a da compra).
  if (first.channel === 'meta_ads')
    steps.push({
      at: fbcClickAt(first.sck, first.at) ?? NO_TIME,
      kind: 'ad',
      label: `Anúncio · ${first.adName ?? first.utmContent ?? 'sem nome'}`,
      ...(first.campaignName ? { detail: first.campaignName } : {}),
    });
  else
    steps.push({
      at: NO_TIME,
      kind: 'cta',
      label: CHANNEL[first.channel],
      ...(first.utmContent ? { detail: `utm_content: ${first.utmContent}` } : {}),
    });
  // Os passos dos pedidos em ordem de hora (o pago pode vir depois do Pix de outro pedido).
  steps.push(...mine.flatMap(STEPS_OF).sort((a, b) => a.at - b.at));
  const up = mine.find((o) => o.status === 'paid' && o.kind === 'upsell');
  const upsellOrder: Order | null = up && up.id !== row.id ? toOrder(up) : null;
  return { order, ...(upsellOrder ? { upsellOrder } : {}), steps };
}

// ---------- módulo do modo ao vivo ----------

/** KPIs da recuperação: saem da fila real, nunca da simulação (a faixa do REC-01 abre todos). */
export const RECOVERY_KPI_IDS = [
  'moneyAtStake',
  'queueOverdue',
  'recoveredRevenue',
  'recoveredCount',
  'recoveryRate72h',
  'cartRecoveryRate',
  'contactSlaMedianMin',
] as const satisfies readonly KpiId[];
const RECOVERY_KPIS: ReadonlySet<KpiId> = new Set(RECOVERY_KPI_IDS);

/** Fila de recuperação, jornada do pedido, KPIs e resumo da recuperação do snapshot. */
export function recoveryModule(ctx: RealCtx): RealModule {
  const { clock } = ctx;
  let recStates: RecoveryStates = {};
  let recOrders: RecoveryOrders = {};
  let data: { at: number; p: Promise<void> } | null = null;
  /** Andamento e pedidos da fila, com cache curto; erro do servidor mantém o último estado. */
  const loadData = (): Promise<void> => {
    if (data && clock() - data.at < DATA_CACHE_MS) return data.p;
    const p = fetchRecoveryData().then(
      (d) => {
        recStates = d.states;
        recOrders = d.orders;
      },
      () => {
        data = null;
      },
    );
    data = { at: clock(), p };
    return p;
  };
  const loadRecovery = async (): Promise<LiveRecoveryItem[]> => {
    const now = clock();
    const [rows] = await Promise.all([
      ctx.load(Math.floor((now - LOOKBACK) / HOUR) * HOUR, Math.ceil(now / 60_000) * 60_000),
      loadData(),
    ]);
    return buildRecovery(rows, recStates, now, ctx.settings(), recOrders);
  };
  /** Fila com os filtros da tela (origem, produto, forma de pagamento). O período não recorta a fila. */
  const loadFiltered = async (q: Pick<Query, 'filters'>): Promise<LiveRecoveryItem[]> =>
    (await loadRecovery().catch(() => [] as LiveRecoveryItem[])).filter((i) => recoveryMatches(i, q));

  async function listRecovery(q: ListQuery): Promise<Page<RecoveryItem>> {
    let items: RecoveryItem[] = await loadFiltered(q);
    if (q.status?.length) items = items.filter((i) => q.status?.includes(i.status));
    if (q.types?.length) items = items.filter((i) => q.types?.includes(i.type));
    if (q.assignee === 'none') items = items.filter((i) => i.assigneeId == null);
    else if (q.assignee && q.assignee !== 'me') items = items.filter((i) => i.assigneeId === q.assignee);
    return paginate(items, q);
  }

  async function recovery(cmd: RecoveryCommand): Promise<RecoveryItem> {
    const st = await saveRecoveryCommand(cmd);
    recStates = { ...recStates, [cmd.itemId]: st };
    // A próxima leitura vai ao servidor (com a marca nova), sem esperar o cache.
    data = null;
    ctx.invalidateOrders();
    const item = (await loadRecovery()).find((i) => i.id === cmd.itemId);
    if (!item) throw Object.assign(new Error('Item não encontrado'), { code: 'not_found' });
    return item;
  }

  /**
   * Pedido aberto pelo id (lista, link antigo, período de 90 dias): procura de trás para frente em
   * janelas de 120 dias (o máximo do servidor) até achar, sem limite de 30 dias. Os pedidos das janelas
   * lidas entram juntos, para a jornada ter as outras compras da mesma pessoa.
   */
  async function getOrderJourney(id: string) {
    const now = clock();
    // Hora cheia nas pontas: abrir de novo no mesmo minuto usa o cache dos pedidos.
    let end = Math.ceil(now / HOUR) * HOUR;
    const rows = new Map<string, RealOrderRow>();
    for (let i = 0; i < JOURNEY_WINDOWS; i++) {
      const from = end - JOURNEY_WINDOW;
      const page = await ctx.load(from, end);
      for (const o of page) rows.set(o.id, o);
      if (rows.has(id)) break;
      end = from;
    }
    return buildJourney([...rows.values()], id);
  }

  /**
   * Detalhe de um KPI da fila (sheet da métrica): o mesmo número da faixa, com a nota de que é o
   * retrato de agora, e o gráfico vazio (a fila não tem série por hora ou dia). Nunca a simulação.
   */
  async function kpiDetail(
    q: Parameters<DataProvider['getKpiDetail']>[0],
  ): Promise<Awaited<ReturnType<DataProvider['getKpiDetail']>> | undefined> {
    if (!RECOVERY_KPIS.has(q.kpi)) return undefined;
    const now = clock();
    const at = q.asOf ?? now;
    const v = recoveryKpis(await loadFiltered(q), at)[q.kpi] ?? kv(q.kpi, null, null, now);
    const r = rangeMs(q, now);
    const axis = buckets([], r.from, r.end, sparkSize(r), now, () => 0).t;
    return {
      value: { ...v, note: v.note ? `${v.note}. ${QUEUE_NOW_NOTE}` : QUEUE_NOW_NOTE },
      series: {
        t: axis,
        series: [{ key: q.kpi, label: q.kpi, role: 'primary', values: axis.map(() => null) }],
        markers: [],
        freshness: v.freshness,
      },
      byCampaign: [],
    };
  }

  return {
    overrides: { recovery, getOrderJourney },
    list: (kind, q) => (kind === 'recovery' ? listRecovery(q) : undefined),
    neverSimKpis: RECOVERY_KPI_IDS,
    kpiDetail,
    async patchSnapshot(q, snap) {
      const rec = await loadFiltered(q);
      // Replay: só o que já existia no instante escolhido (valor e contagens do mesmo conjunto).
      const at = q.asOf ?? clock();
      snap.recoverySummary = recoverySummary(rec, at);
      Object.assign(snap.kpis, recoveryKpis(rec, at));
    },
  };
}
